import {useTranslation} from '@/translations/i18n'
import {FollowUp, TreatmentRequestExtended} from '@/features/treatment-requests/types'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {formatDate} from '@/dayjs'
import {TreatmentRequestFollowUpMenu} from '@/features/treatment-requests/components/treatment-request-follow-up-menu/TreatmentRequestFollowUpMenu'
import {StyledFollowUpCounterNote, StyledFollowUpCounterText, StyledTreatmentRequestFollowUpItem} from './style'
import {useState} from 'react'
import {TreatmentRequestEditFollowUp} from '@/features/treatment-requests/components/treatment-request-edit-follow-up/TreatmentRequestEditFollowUp'
import {useDeleteFollowUp} from '@/features/treatment-requests/services/useDeleteFollowUp'
import {errorHandler} from '@/utilities/helpers'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useOutletContext} from 'react-router-dom'

interface TreatmentRequestFollowUpItemProps {
    followUp: FollowUp
    isLast: boolean
    number: number
    isDisabled: boolean
    onToggleEditFollowUp: (value: boolean) => void
}

export const TreatmentRequestFollowUpItem = ({
    followUp,
    number,
    isLast,
    isDisabled,
    onToggleEditFollowUp
}: TreatmentRequestFollowUpItemProps) => {
    const {t} = useTranslation()
    const [followUpToEdit, setFollowUpToEdit] = useState<null | FollowUp>(null)

    const {treatmentRequest} = useOutletContext<{treatmentRequest: TreatmentRequestExtended}>()

    const deleteFollowUpMutation = useDeleteFollowUp(followUp.id, {
        onSuccess: () => {
            void queryClient.cancelQueries({queryKey: [QUERY_KEYS.TREATMENT_REQUESTS, treatmentRequest.id]})
            void queryClient.setQueriesData(
                {queryKey: [QUERY_KEYS.TREATMENT_REQUESTS, treatmentRequest.id]},
                {
                    ...treatmentRequest,
                    followups: treatmentRequest.followups?.filter(followUpQuery => followUpQuery.id != followUp.id)
                }
            )
        },
        onError: errorHandler
    })

    const handleClickEdit = (show: boolean) => {
        onToggleEditFollowUp(show)
        if (show) {
            setFollowUpToEdit(followUp)
        } else {
            setFollowUpToEdit(null)
        }
    }

    if (followUpToEdit) {
        return (
            <TreatmentRequestEditFollowUp
                followUpToEdit={followUpToEdit}
                onCancel={() => handleClickEdit(false)}
                isLastFollowUp={isLast}
            />
        )
    }

    return (
        <StyledTreatmentRequestFollowUpItem direction="column" gap={1} fullWidth $isDisabled={isDisabled}>
            <Flexbox fullWidth align="center">
                <StyledFollowUpCounterText>
                    #{number} {t('treatment_requests:follow_up', {count: 1})}
                </StyledFollowUpCounterText>
                <TreatmentRequestFollowUpMenu
                    onClickEdit={() => handleClickEdit(true)}
                    onClickDelete={() => deleteFollowUpMutation.mutate()}
                />
            </Flexbox>
            {followUp.date && <p>{formatDate(followUp.date, 'DD/MM/YYYY')}</p>}
            {followUp.notes && <StyledFollowUpCounterNote>{followUp.notes}</StyledFollowUpCounterNote>}
        </StyledTreatmentRequestFollowUpItem>
    )
}
